import { Button, Col, Form, Input, InputNumber, Modal, Table, TablePaginationConfig, notification } from "antd";
import { FilterValue } from "antd/lib/table/interface";
import { useEffect, useState } from "react";
import { PlusCircleTwoTone } from "@ant-design/icons";
import "../../shared/styles/list.css";
import { currencyColumns } from "./columns";
import { getCurrencies, createCurrency, updateCurrency, deleteCurrency } from "../../services/currencyApi";
import ContainerLayout from "../common/containerLayout";

export interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters: Record<string, FilterValue | null>;
}

export interface DataType {
  id: number;
  name?: string;
  code?: string;
  base: string;
  rate: number;
  spread?: number;
  displayRate?: number;
}

const CurrencyList = () => {
  const [data, setData] = useState<DataType[]>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<DataType | null>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    const data = await getCurrencies();
    setData(data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = () => {
    setEditingCurrency(null);
    form.resetFields();
    form.setFieldValue('base', 'USD');
    setModalOpen(true);
  };

  const handleEdit = (record: DataType) => {
    setEditingCurrency(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    await deleteCurrency(id);
    notification.success({ message: "Moneda eliminada" });
    fetchData();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (editingCurrency) {
        await updateCurrency(editingCurrency.id, values);
        notification.success({ message: "Moneda actualizada" });
      } else {
        await createCurrency(values);
        notification.success({ message: "Moneda creada" });
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      if (error instanceof Error) {
        notification.error({ message: error.message });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContainerLayout title="Denominaciones" showNotification={false}>
      <Col span={24}>
        <Button
          type="primary"
          icon={<PlusCircleTwoTone />}
          onClick={handleCreate}
          style={{ marginBottom: 16 }}
        >
          Nueva moneda
        </Button>
        <Table
          style={{ minWidth: "100%" }}
          columns={currencyColumns({ handleEdit, handleDelete })}
          rowKey={(record) => record.id}
          dataSource={data}
          scroll={{ y: "calc(100vh - 370px)" }}
        />
      </Col>
      <Modal
        title={editingCurrency ? "Editar moneda" : "Nueva moneda"}
        open={modalOpen}
        onOk={handleOk}
        onCancel={() => setModalOpen(false)}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Nombre" rules={[{ required: true, message: "Ingrese el nombre" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label="Código" rules={[{ required: true, message: "Ingrese el código" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="base" label="Base" rules={[{ required: true, message: "Ingrese la base" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="rate" label="Tasa" rules={[{ required: true, message: "Ingrese la tasa" }]}>
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="spread" label="Spread">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </ContainerLayout>
  );
};

export default CurrencyList;
